// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Injectable, inject, TemplateRef } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse
} from './metric-chart-base.entity';
import { TEMPLATE_IDENTIFIERS } from '@lfx/key-projects/key-projects.model';

/**
 * Committee member data model from SQL query (API returns uppercase keys)
 */
export interface CommitteeMemberData {
  MEMBER_ID: string;
  FIRST_NAME: string;
  LAST_NAME: string;
  MEMBER_DISPLAY_NAME: string;
  PLATFORM_VALUE: string;
  AVATAR_URL?: string;
  CONTACT_TITLE: string;
  CONTACT_ROLE: string;
  APPOINTED_BY: string;
  CREATED_DATE: string;
  ORGANIZATION_NAME: string;
  COMMITTEE_NAME: string;
  IS_BOARD_MEMBER: boolean;
  PROJECT_SLUG: string;
  PROJECT_NAME: string;
  FOUNDATION_NAME: string;
}

/**
 * Committee member summary data model from SQL query
 */
export interface CommitteeMemberSummaryData {
  totalCommitteeMembers: number;
}

/**
 * Entity service for committee members metric type
 * Handles SQL query: ANALYTICS.PLATINUM_ORGANIZATION_DASHBOARD.COMMITTEE_MEMBERS_LIST
 */
@Injectable({
  providedIn: 'root'
})
export class CommitteeMembersMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);

  private readonly config = {
    definition: 'Individual who is on a foundation committee, such as advisory groups, steering committees, and marketing committees.',
    columns: [
      { name: 'Our committee members', prop: 'displayName', templateId: TEMPLATE_IDENTIFIERS.USER_PROFILE },
      { name: 'Committee', prop: 'committee' },
      { name: 'Date joined', prop: 'dateJoined' }
    ],
    summaryColumns: [
      { name: 'Definition', prop: 'definition' },
      { name: 'Total count for this project', prop: 'totalCount' },
      { name: 'Data source', prop: 'dataSource' }
    ],
    dataSource: 'LFX'
  };

  /**
   * Get columns for committee members table
   * Based on UI requirements from screenshot
   */
  getColumns(): Column[] {
    return this.config.columns.map(col => {
      const { templateId, ...columnProps } = col;
      return {
        ...columnProps,
        ...(templateId && { template: templateId as unknown as TemplateRef<unknown> })
      };
    });
  }

  /**
   * Get API endpoint for committee members
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/committee-members`;
  }

  /**
   * Fetch committee members data from backend
   * Executes SQL: SELECT contact_id AS member_id, first_name, last_name, CONCAT(first_name, ' ', last_name) AS member_display_name,
   * user_name AS platform_value, user_photo_url AS avatar_url, contact_title, contact_role, appointed_by,
   * created_date, account_name AS organization_name, committee_name, is_board_member, project_slug,
   * project_name, foundation_name FROM ANALYTICS.PLATINUM_ORGANIZATION_DASHBOARD.COMMITTEE_MEMBERS_LIST
   * WHERE is_board_member = FALSE AND project_slug = '{project_slug}' AND account_id = '{account_id}'
   * ORDER BY created_date DESC, last_name ASC, first_name ASC
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<MetricChartApiResponse<CommitteeMemberData>>(
          this.getEndpoint(),
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch committee members data');
            }

            return response.data.map(committeeMember => this.transformToDataRow(committeeMember));
          }),
          catchError(error => {
            console.error('Error fetching committee members data:', error);
            return of([]);
          })
        );
      }),
      catchError(error => {
        console.error('Error in committee members entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform committee member data to DataRow format for the table
   */
  private transformToDataRow(committeeMember: CommitteeMemberData): DataRow {
    return {
      displayName: {
        text: committeeMember.MEMBER_DISPLAY_NAME,
        extraData: {
          imageUrl: committeeMember.AVATAR_URL,
          userId: committeeMember.MEMBER_ID,
          memberId: committeeMember.MEMBER_ID,
          memberDisplayName: committeeMember.MEMBER_DISPLAY_NAME,
          memberLogoUrl: committeeMember.AVATAR_URL
        }
      },
      committee: { text: committeeMember.COMMITTEE_NAME },
      dateJoined: { text: this.formatDate(committeeMember.CREATED_DATE) }
    };
  }

  /**
   * Fetch committee members summary data from backend
   * Executes SQL: SELECT COUNT(DISTINCT contact_id) AS total_committee_members
   * FROM ANALYTICS.PLATINUM_ORGANIZATION_DASHBOARD.COMMITTEE_MEMBERS_LIST
   * WHERE is_board_member = FALSE AND project_slug = '{project_slug}' AND account_id = '{account_id}'
   */
  getSummaryData(params: MetricChartData): Observable<{columns: Column[], data: DataRow[]}> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<{success: boolean; message?: string; data: CommitteeMemberSummaryData}>(
          `${this.getEndpoint()}/summary`,
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch committee members summary');
            }

            // Define columns for summary table
            const summaryColumns: Column[] = this.config.summaryColumns;

            // Transform API response to summary table format
            const summaryData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalCount: { text: response.data.totalCommitteeMembers.toLocaleString('en-US') },
                dataSource: { text: this.config.dataSource }
              }
            ];

            return { columns: summaryColumns, data: summaryData };
          }),
          catchError(error => {
            console.error('Error fetching committee members summary:', error);
            // Return fallback data on error
            const fallbackColumns: Column[] = this.config.summaryColumns;
            const fallbackData: DataRow[] = [
              {
                definition: { text: this.config.definition },
                totalCount: { text: 'N/A' },
                dataSource: { text: this.config.dataSource }
              }
            ];
            return of({ columns: fallbackColumns, data: fallbackData });
          })
        );
      }),
      catchError(error => {
        console.error('Error in committee members summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = this.config.summaryColumns;
        const fallbackData: DataRow[] = [
          {
            definition: { text: this.config.definition },
            totalCount: { text: 'N/A' },
            dataSource: { text: this.config.dataSource }
          }
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }

  /**
   * Format date string for display
   */
  private formatDate(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString; // Return original if parsing fails
    }
  }
}
